import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { isDefined } from '@e-socna/utils';

import {
  PATH_MY_ACCOUNT_DEFAULT,
  PATH_MY_ACCOUNT_PERSON_ADDRESSES,
  PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT,
  PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT_NEW,
  PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT_MODIFY,
  PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT_DELETE,
  PATH_MY_ACCOUNT_PERSON_BANK,
  PATH_MY_ACCOUNT_PERSON_BANK_EDIT,
  PATH_MY_ACCOUNT_PERSON_BILLING,
  PATH_MY_ACCOUNT_PERSON_BILLING_EDIT,
  PATH_MY_ACCOUNT_PERSON_CONTACT,
  PATH_MY_ACCOUNT_PERSON_CONTACT_EDIT,
  PATH_MY_ACCOUNT_PERSON_DEFAULT,
  PATH_MY_ACCOUNT_PERSON_GENERAL,
  PATH_MY_ACCOUNT_PERSON_GENERAL_EDIT,
  PATH_MY_ACCOUNT_PERSON_MEMBERSHIPS,
  PATH_MY_ACCOUNT_PERSON_MEMBERSHIPS_EDIT,
  PATH_MY_ACCOUNT_PERSON_PARTICIPATIONS,
  PATH_MY_ACCOUNT_PERSON_PENNYLANE,
  PATH_MY_ACCOUNT_USER_ACCOUNT,
  PATH_MY_ACCOUNT_USER_AUTHORIZATIONS,
  PATH_MY_ACCOUNT_USER_DEFAULT,
  PATH_MY_ACCOUNT_USER_SETTINGS,
  PATH_MY_ACCOUNT_PERSON_VERSIONS,
  PATH_MY_ACCOUNT_PERSON_ADMIN,
  PATH_MY_ACCOUNT_PERSON_ADMIN_EDIT,
} from '../../app/constants/paths.constant';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Loading } from '../../components/Loading';
import { Tabs, TabsStrategy } from '../../components/Tabs';
import { useAuthenticationContext } from '../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { useGraphQLQuery } from '../../hooks/useGraphQLQuery.hook';
import { Page } from '../../contexts/NavigationContext/enums/page.enum';
import { useNavigationContext } from '../../contexts/NavigationContext/hooks/useNavigationContext.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { User } from './User';
import { MyAccountPage_OneUser_QueryDocument } from './queries/one-user.query';
import { Person } from './Person';

export const MyAccountPage = () => {
  const { setPage } = useNavigationContext();
  const { authenticationData } = useAuthenticationContext();
  const { addressId } = useParams();

  const id = authenticationData?._id;

  const { data, isSuccess } = useGraphQLQuery(
    MyAccountPage_OneUser_QueryDocument,
    undefined,
    { enabled: !!id },
  );

  const { currentPathKey } = useBuildPaths(
    {
      account: PATH_MY_ACCOUNT_USER_ACCOUNT,
      default: PATH_MY_ACCOUNT_USER_DEFAULT,
      authorizations: PATH_MY_ACCOUNT_USER_AUTHORIZATIONS,
      settings: PATH_MY_ACCOUNT_USER_SETTINGS,
    },
    { addressId },
  );

  const activeTab = isDefined(currentPathKey) ? 'user' : 'person';

  useEffect(() => {
    setPage(Page.Unregistered);
  }, [setPage]);

  const titleContent = (
    <>
      <Helmet>
        <title>{buildDocumentTitle('Mon Compte')}</title>
      </Helmet>
      <Breadcrumbs
        paths={[
          {
            to: PATH_MY_ACCOUNT_DEFAULT,
            label: 'mon compte',
            key: 'myAccount',
          },
        ]}
      />
      <PageTitle title="Mon Compte" />
    </>
  );

  if (isSuccess) {
    const user = data.findAuthenticatedUser;
    const person = data.findAuthenticatedUser.person;

    const userPaths = {
      account: PATH_MY_ACCOUNT_USER_ACCOUNT,
      authorizations: PATH_MY_ACCOUNT_USER_AUTHORIZATIONS,
      settings: PATH_MY_ACCOUNT_USER_SETTINGS,
    } as const;

    const personPaths = {
      addresses: PATH_MY_ACCOUNT_PERSON_ADDRESSES,
      addressesEdit: PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT,
      addressesEditDelete: PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT_DELETE,
      addressesEditModify: PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT_MODIFY,
      addressesEditNew: PATH_MY_ACCOUNT_PERSON_ADDRESSES_EDIT_NEW,
      admin: PATH_MY_ACCOUNT_PERSON_ADMIN,
      adminEdit: PATH_MY_ACCOUNT_PERSON_ADMIN_EDIT,
      bankInfo: PATH_MY_ACCOUNT_PERSON_BANK,
      bankInfoEdit: PATH_MY_ACCOUNT_PERSON_BANK_EDIT,
      billing: PATH_MY_ACCOUNT_PERSON_BILLING,
      billingEdit: PATH_MY_ACCOUNT_PERSON_BILLING_EDIT,
      contactInfo: PATH_MY_ACCOUNT_PERSON_CONTACT,
      contactInfoEdit: PATH_MY_ACCOUNT_PERSON_CONTACT_EDIT,
      default: PATH_MY_ACCOUNT_DEFAULT,
      generalInfo: PATH_MY_ACCOUNT_PERSON_GENERAL,
      generalInfoEdit: PATH_MY_ACCOUNT_PERSON_GENERAL_EDIT,
      memberships: PATH_MY_ACCOUNT_PERSON_MEMBERSHIPS,
      membershipsEdit: PATH_MY_ACCOUNT_PERSON_MEMBERSHIPS_EDIT,
      participations: PATH_MY_ACCOUNT_PERSON_PARTICIPATIONS,
      pennylane: PATH_MY_ACCOUNT_PERSON_PENNYLANE,
      versions: PATH_MY_ACCOUNT_PERSON_VERSIONS,
    } as const;

    return (
      <>
        {titleContent}
        <Tabs
          activeTab={activeTab}
          id="my-user-account-tabs"
          strategy={TabsStrategy.Controlled}
          tabs={[
            {
              key: 'user',
              label: 'Mon Compte',
              panel: <User paths={userPaths} user={user} />,
              value: 'user',
              to: PATH_MY_ACCOUNT_USER_DEFAULT,
            },
            {
              key: 'person',
              label: 'Mes Informations',
              panel: <Person paths={personPaths} person={person} />,
              value: 'person',
              to: PATH_MY_ACCOUNT_PERSON_DEFAULT,
            },
          ]}
          title="Onglets de l'écran Mon Compte"
        />
      </>
    );
  }

  return (
    <>
      <Loading />
      {titleContent}
    </>
  );
};
